import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import WebTileLayer from '@arcgis/core/layers/WebTileLayer';
import TileInfo from '@arcgis/core/layers/support/TileInfo';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer';
import {
  ProjectReferenceLayerService,
  ReferenceLayerDetail,
  ReferenceLayerSummary,
} from './project-reference-layer.service';
import {
  ProjectMapManifestService, ProjectMapAsset, ProjectMapPreferences,
  ProjectMapStyle, ProjectMapManifest, PublishedMapLayer, ProjectMapPreferencePatch,
} from './project-map-manifest.service';
import { MAP_VIEW_BY_COLORS } from '../../modules/projects/components/maps/map.constants';
import { firstValueFrom, fromEvent, Subject, takeUntil } from 'rxjs';
import { cornersToWebMercator, hasValidCorners } from '../helpers/reference-layer-image';
import { filteredDrawEligible, LiveDrawFilter, liveDrawEligible, publishedSymbolFor } from '../helpers/live-published-features';
import { LivePublishedFeatureOverlay } from './live-published-feature-overlay';
import { StatusIcon, statusMarkerDataUrl, statusMarkerMetrics } from '../helpers/status-marker';

// __esri.Layer is the common supertype — KMLLayer, GeoJSONLayer, and FeatureLayer
// all extend it. We keep the union narrow since the rest of this file only
// references `.visible` and `.id` on the instance, both present on Layer.
export type ActiveRefLayer = {
  summary: ReferenceLayerSummary;
  layer: __esri.Layer;
  published?: PublishedMapLayer;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error?: string;
};

/**
 * Convert a `#RRGGBB` hex string from MAP_VIEW_BY_COLORS into the RGBA tuple
 * ArcGIS symbol constructors expect.
 */
function hexToRgba(hex: string, alpha: number): [number, number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255, alpha];
}

/**
 * Assign a color to a reference layer by its position in the list. Cycles
 * through the shared MAP_VIEW_BY_COLORS palette (same palette powering the
 * "Color by" map view modes) so overlay colors feel consistent with the
 * rest of the map. Skips the first palette entry (Violet Blue) because the
 * site boundary — always rendered — already uses it, and we don't want
 * overlays to blend into it.
 */
function overlayColor(index: number): [number, number, number, number] {
  const palette = MAP_VIEW_BY_COLORS.slice(1);
  const hex = palette[index % palette.length];
  return hexToRgba(hex, 0.9);
}

// ArcGIS uses scale constraints, while the immutable rendition contract uses
// Web Mercator XYZ zooms. This is the standard scale at zoom zero.
function zoomScale(zoom: number | undefined): number | undefined {
  return Number.isInteger(zoom) && zoom! >= 0 ? 591657527.591555 / (2 ** zoom!) : undefined;
}

/** Point status markers (Addendum 3, Marker spec v3.1, size FINAL): the layer's point size and outline, status fill. */
function statusMarkerSymbol(status: StatusIcon, style: ProjectMapStyle | undefined): PictureMarkerSymbol {
  const metrics = statusMarkerMetrics(style?.symbols);
  return new PictureMarkerSymbol({ url: statusMarkerDataUrl(status, metrics), width: `${metrics.diameter}px`, height: `${metrics.diameter}px` });
}

interface MapScope { activation: number; projectId: string; accountId: string; signal: AbortSignal };
interface PreferenceEdit { patch: ProjectMapPreferencePatch; resolve: () => void; reject: (error: unknown) => void };

@Injectable()
export class MapReferenceLayerService {
  private activeLayers: Map<string, ActiveRefLayer> = new Map();
  // Raw reference-layer API list (created_at DESC), refreshed whenever
  // activate()/appendLegacySummaries() fetch it. Used only to compute the
  // legacy-only ordinal fallback in loadEntry() — activeLayers mixes in
  // published catalog layers ahead of legacy ones, so its own key order
  // can't be used for that (a published layer shifts every legacy index).
  private legacySummaries: ReferenceLayerSummary[] = [];
  private blobUrls: Map<string, string> = new Map();
  private activation = 0;
  private controller = new AbortController();
  private projectId: string;
  private accountId: string;
  private currentAccount: () => string | undefined = () => undefined;
  private refreshSequence = 0;
  private preferenceEpoch = 0;
  private confirmedOverrides: ProjectMapPreferences['overrides'] = {};
  private preferenceEdits: PreferenceEdit[] = [];
  private preferenceRunning = false;
  private mapView: __esri.MapView;
  private pending = new Map<string, Promise<void>>();
  readonly changed = new Subject<void>();

  private publishedProject = false;
  private catalogRevision: number | null = null;
  private preferenceVersion = 0;
  private publishedOverrides: ProjectMapPreferences['overrides'] = {};
  private manifestEtag?: string;
  private manifest?: ProjectMapManifest;
  private grantTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Live-first drawing per published layer id (live-source layers only); see LivePublishedFeatureOverlay. */
  private liveOverlays = new Map<string, LivePublishedFeatureOverlay>();
  /**
   * Per published layer id: draw only matching features (the picker's feature_source `where`). Set by the picker's
   * own instance before activation; the project map never sets one. Survives deactivate (it is session config).
   */
  private drawFilters = new Map<string, LiveDrawFilter>();

  constructor(
    private referenceLayerApi: ProjectReferenceLayerService,
    private manifestApi: ProjectMapManifestService,
  ) {}

  /**
   * Fetch the project's reference-layer summaries and build ArcGIS layer
   * objects for them, but don't add them to the map yet. The caller is
   * responsible for calling showOnMap() when the layers should actually
   * render (e.g. when the Swipe widget activates).
   */
  async activateForProject(mapView: __esri.MapView, projectId: string, accountId: string,
    currentAccount: () => string | undefined = () => accountId): Promise<ActiveRefLayer[]> {
    if (!accountId || currentAccount() !== accountId) {
      this.deactivate();
      throw Object.assign(new Error('Authentication required'), { status: 401 });
    }
    const refresh = ++this.refreshSequence;
    let response: Awaited<ReturnType<ProjectMapManifestService['manifest']>>;
    if (this.mapView === mapView && this.projectId === projectId && this.accountId === accountId
      && this.publishedProject && this.manifestEtag) {
      const scope = this.captureScope();
      const preferenceEpoch = this.preferenceEpoch;
      try {
        response = await this.manifestApi.manifest(projectId, this.manifestEtag, scope.signal);
        if (!this.isCurrent(scope) || refresh !== this.refreshSequence) return [];
        if (response.notModified) {
          const preferences = await this.manifestApi.preferences(projectId, scope.signal);
          if (!this.isCurrent(scope) || refresh !== this.refreshSequence) return [];
          if (preferenceEpoch === this.preferenceEpoch && !this.preferenceEdits.length) {
            this.acceptPreferences(preferences);
            this.reconcilePreferences();
          }
          return this.getActiveLayers();
        }
      } catch (error) {
        if (!this.isCurrent(scope) || refresh !== this.refreshSequence) return [];
        this.deactivate();
        throw error;
      }
    }
    this.deactivate();
    this.projectId = projectId;
    this.accountId = accountId;
    this.currentAccount = currentAccount;
    this.mapView = mapView;
    const scope = this.captureScope();
    const { signal } = scope;
    // Only a 404 from the manifest request can enter the legacy registry path.
    let legacy = false;
    if (!response) {
      try { response = await this.manifestApi.manifest(projectId, undefined, signal); }
      catch (error) {
        if (!this.isCurrent(scope)) return [];
        if (error?.status !== 404) throw error;
        legacy = true;
      }
    }
    if (!this.isCurrent(scope)) return [];
    if (!legacy) {
      const { manifest, etag } = response;
      if (!manifest || manifest.projectId !== projectId) throw new Error('Invalid published map catalog');
      const preferences = await this.manifestApi.preferences(projectId, signal);
      if (!this.isCurrent(scope)) return [];
      this.publishedProject = true;
      this.manifest = manifest;
      this.manifestEtag = etag;
      this.catalogRevision = manifest.catalogRevision;
      this.acceptPreferences(preferences);
      this.publishedOverrides = { ...this.confirmedOverrides };
      const groups = new Map(manifest.groups.map(group => [group.id, group.order]));
      const layers = [...manifest.layers].sort((left, right) =>
        (groups.get(left.groupId) || 0) - (groups.get(right.groupId) || 0)
        || left.order - right.order || left.id.localeCompare(right.id));
      for (const published of layers) {
        const summary: ReferenceLayerSummary = {
          id: published.id, name: published.name, source_type: 'published_catalog', source_url: null,
          feature_count: 0,
          extent: { xmin: published.bounds[0], ymin: published.bounds[1], xmax: published.bounds[2], ymax: published.bounds[3] },
          source_filename: null, created_at: '', updated_at: '',
        };
        // The published zoom range is a tiling concern: only the tile children
        // (below) are scale-limited. Vector children draw at every zoom, so a
        // small layer never vanishes when the reader zooms in or out.
        const group = new GroupLayer({ id: `published-map-layer-${published.id}`, title: published.name,
          visible: false, opacity: this.layerState(published).opacity, visibilityMode: 'independent' });
        this.activeLayers.set(summary.id, { summary, layer: group, published, status: 'idle' });
      }
      await this.appendLegacySummaries(scope, new Set(manifest.layers.map(layer => layer.id)));
      if (!this.isCurrent(scope)) return [];
      return this.getActiveLayers();
    }
    this.publishedProject = false;
    this.manifest = undefined;
    this.manifestEtag = undefined;
    let summaries: ReferenceLayerSummary[];
    try {
      summaries = await firstValueFrom(this.referenceLayerApi.list(projectId)
        .pipe(takeUntil(fromEvent(signal, 'abort'))));
    } catch (error) {
      if (signal.aborted) return [];
      throw error;
    }
    if (!this.isCurrent(scope)) return [];
    if (!summaries?.length) return [];
    this.legacySummaries = summaries;
    for (const summary of summaries) {
      // Stable lightweight wrappers let Swipe keep its layer references while
      // geometry loads lazily and prevent asynchronous children changing order.
      const layer = new GroupLayer({
        id: `reference-layer-${summary.id}`, title: summary.name,
        visible: false, opacity: 1, visibilityMode: 'independent',
      });
      this.activeLayers.set(summary.id, { summary, layer, status: 'idle' });
    }
    return Array.from(this.activeLayers.values());
  }

  private async appendLegacySummaries(scope: MapScope, publishedIds: Set<string>): Promise<void> {
    let summaries: ReferenceLayerSummary[];
    try {
      summaries = await firstValueFrom(this.referenceLayerApi.list(scope.projectId)
        .pipe(takeUntil(fromEvent(scope.signal, 'abort'))));
    } catch {
      // A legacy list problem must not hide an authorized published catalog.
      if (scope.signal.aborted) return;
      return;
    }
    if (!this.isCurrent(scope)) return;
    this.legacySummaries = summaries || [];
    for (const summary of summaries || []) {
      if (publishedIds.has(summary.id) || summary.map_source_id || summary.map_withdrawn) continue;
      if (this.activeLayers.has(summary.id)) continue;
      const layer = new GroupLayer({
        id: `reference-layer-${summary.id}`, title: summary.name,
        visible: false, opacity: 1, visibilityMode: 'independent',
      });
      this.activeLayers.set(summary.id, { summary, layer, status: 'idle' });
    }
  }

  getPublishedInspectionScope(): { projectId: string; accountId: string; catalogRevision: number } | null {
    if (!this.publishedProject || !this.manifest || !this.isCurrent(this.captureScope())) return null;
    return { projectId: this.projectId, accountId: this.accountId, catalogRevision: this.catalogRevision };
  }

  getActiveLayers(): ActiveRefLayer[] {
    if (this.projectId && !this.isCurrent(this.captureScope())) return [];
    return Array.from(this.activeLayers.values());
  }

  /**
   * Exclusive show: layers passing `filter` are added (if needed) and made
   * visible; every other reference layer is hidden. This matters when the
   * caller is switching the active swipe layer — the previously active
   * layer must become invisible or it would render everywhere on the map
   * (outside the swipe divider's control).
   */
  async showOnMap(mapView: __esri.MapView, filter?: (entry: ActiveRefLayer) => boolean): Promise<void> {
    if (mapView !== this.mapView || !this.isCurrent(this.captureScope())) return;
    const loads: Promise<void>[] = [];
    let referenceIndex = 0;
    // ArcGIS draws higher collection indices above lower ones; the published
    // catalog's first row must be the highest reference, below work graphics.
    const entries = Array.from(this.activeLayers.values());
    if (this.publishedProject) entries.reverse();
    for (const entry of entries) {
      const shouldShow = !filter || filter(entry);
      if (shouldShow) {
        if (!mapView.map.layers.includes(entry.layer as any)) {
          mapView.map.add(entry.layer, referenceIndex);
        }
        mapView.map.reorder(entry.layer, referenceIndex++);
        entry.layer.visible = true;
        loads.push(this.loadEntry(entry));
      } else {
        entry.layer.visible = false;
      }
    }
    await Promise.all(loads);
  }

  setOpacity(layerId: string, opacity: number): void {
    if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) return;
    const entry = this.activeLayers.get(layerId);
    if (entry) entry.layer.opacity = opacity;
  }

  setLabels(layerId: string, labels: boolean): void {
    const apply = (layer: any) => {
      if (layer.__projectMapLabelLayer) layer.visible = labels;
      layer.layers?.forEach((child: any) => apply(child));
    };
    const parent = this.activeLayers.get(layerId)?.layer;
    if (parent) apply(parent);
  }

  isPublishedCatalog(): boolean { return this.publishedProject; }

  /** Empty for an ungrouped layer so the panel draws no group heading for it. */
  publishedGroupName(layer: PublishedMapLayer): string {
    if (!layer.groupId) return '';
    return this.manifest?.groups.find(group => group.id === layer.groupId)?.name || 'Project layers';
  }

  layerState(layer: PublishedMapLayer): { visible: boolean; opacity: number; labels: boolean } {
    const override = this.publishedOverrides[layer.id] || {};
    return { visible: override.visible ?? layer.defaultVisible, opacity: override.opacity ?? layer.defaultOpacity,
      labels: override.labels ?? layer.labelDefault };
  }

  private captureScope(): MapScope {
    return { activation: this.activation, projectId: this.projectId, accountId: this.accountId,
      signal: this.controller.signal };
  }

  private isCurrent(scope: MapScope): boolean {
    if (scope.activation !== this.activation || scope.signal.aborted || scope.projectId !== this.projectId
      || scope.accountId !== this.accountId) return false;
    if (this.currentAccount() !== scope.accountId) { this.deactivate(); this.changed.next(); return false; }
    return true;
  }

  private requireScope(scope: MapScope): void {
    if (!this.isCurrent(scope)) throw new DOMException('Map account or project changed', 'AbortError');
  }

  private acceptPreferences(preferences: ProjectMapPreferences): void {
    this.preferenceVersion = preferences.version;
    this.confirmedOverrides = preferences.overrides || {};
    this.preferenceEpoch++;
  }

  private reconcilePreferences(): void {
    const next = { ...this.confirmedOverrides };
    for (const edit of this.preferenceEdits) {
      for (const [id, patch] of Object.entries(edit.patch)) {
        if (patch === null) delete next[id];
        else next[id] = { ...next[id], ...patch };
      }
    }
    this.publishedOverrides = next;
    this.activeLayers.forEach(entry => {
      if (!entry.published) return;
      const state = this.layerState(entry.published);
      entry.layer.opacity = state.opacity;
      entry.layer.visible = state.visible;
      this.setLabels(entry.summary.id, state.labels);
    });
    this.changed.next();
  }

  savePublishedViewState(layerId: string, patch: Partial<{ visible: boolean; opacity: number; labels: boolean }>): Promise<void> {
    if (!this.publishedProject || !this.activeLayers.has(layerId)) return Promise.resolve();
    return this.enqueuePreferenceEdit({ [layerId]: { ...patch } });
  }

  resetPublishedViewState(): Promise<void> {
    if (!this.publishedProject) return Promise.resolve();
    const overrides: ProjectMapPreferencePatch = {};
    this.activeLayers.forEach((entry, id) => { if (entry.published) overrides[id] = null; });
    return this.enqueuePreferenceEdit(overrides);
  }

  private enqueuePreferenceEdit(patch: ProjectMapPreferencePatch): Promise<void> {
    const scope = this.captureScope();
    if (!this.isCurrent(scope)) return Promise.resolve();
    const promise = new Promise<void>((resolve, reject) => this.preferenceEdits.push({ patch, resolve, reject }));
    this.preferenceEpoch++;
    this.reconcilePreferences();
    if (!this.preferenceRunning) void this.drainPreferenceEdits(scope);
    return promise;
  }

  private async drainPreferenceEdits(scope: MapScope): Promise<void> {
    this.preferenceRunning = true;
    try {
      while (this.isCurrent(scope) && this.preferenceEdits.length) {
        const edit = this.preferenceEdits[0];
        try {
          let saved: ProjectMapPreferences;
          try {
            saved = await this.manifestApi.patchPreferences(scope.projectId, this.preferenceVersion, edit.patch, scope.signal);
          } catch (error) {
            this.requireScope(scope);
            if (error?.status !== 409) throw error;
            const latest = await this.manifestApi.preferences(scope.projectId, scope.signal);
            this.requireScope(scope);
            this.acceptPreferences(latest);
            saved = await this.manifestApi.patchPreferences(scope.projectId, latest.version, edit.patch, scope.signal);
          }
          this.requireScope(scope);
          this.acceptPreferences(saved);
          this.preferenceEdits.shift();
          this.reconcilePreferences();
          edit.resolve();
        } catch (error) {
          if (!this.isCurrent(scope)) return;
          this.preferenceEdits.shift();
          if ([401, 403].includes(error?.status)) { this.deactivate(); this.changed.next(); }
          else this.reconcilePreferences();
          edit.reject(error);
        }
      }
    } finally {
      if (this.isCurrent(scope)) this.preferenceRunning = false;
    }
  }

  private loadEntry(entry: ActiveRefLayer): Promise<void> {
    if (entry.status === 'ready') return Promise.resolve();
    const inFlight = this.pending.get(entry.summary.id);
    if (inFlight) return inFlight;
    const scope = this.captureScope();
    const { activation, signal } = scope;
    entry.status = 'loading';
    entry.error = undefined;
    this.changed.next();
    const pending = (entry.published
      ? this.buildPublishedLayer(entry, signal)
      // Fallback index (used only when summary.palette_index is null) must be
      // the row's ordinal among legacy-colored summaries alone — not its
      // position in activeLayers, which lists published catalog layers first
      // and would shift every legacy index by the catalog's layer count.
      : this.buildLayerFromSummary(this.projectId, entry.summary,
        this.referenceLayerApi.legacyPaletteOrdinal(this.legacySummaries, entry.summary.id), signal))
      .then(async (layer) => {
        if (!layer) throw new Error('This layer has no supported geometry.');
        if (!this.isCurrent(scope) || this.activeLayers.get(entry.summary.id) !== entry) { layer.destroy(); return; }
        (entry.layer as GroupLayer).add(layer);
        // A group's metadata can load while a geometry child has failed.
        // Wait for the complete content before reporting the row as ready.
        if (layer.type === 'group') await (layer as GroupLayer).loadAll();
        else await layer.load();
        if (this.isCurrent(scope) && this.activeLayers.get(entry.summary.id) === entry) entry.status = 'ready';
      })
      .catch((error) => {
        if (!this.isCurrent(scope)) return;
        if ([401, 403].includes(error?.status)) { this.deactivate(); this.changed.next(); return; }
        this.clearEntryResources(entry);
        const group = entry.layer as GroupLayer;
        group.layers.toArray().forEach(layer => layer.destroy());
        group.layers.removeAll();
        for (const [key, url] of this.blobUrls) {
          if (key.startsWith(`${entry.summary.id}:`)) {
            URL.revokeObjectURL(url);
            this.blobUrls.delete(key);
          }
        }
        entry.status = 'error';
        entry.error = 'Unable to load this layer. Toggle it off and on to retry.';
      })
      .finally(() => {
        if (activation !== this.activation) return;
        this.pending.delete(entry.summary.id);
        this.changed.next();
      });
    this.pending.set(entry.summary.id, pending);
    return pending;
  }

  /**
   * Draw only the features of `layerId` that `filter` matches, never its unfiltered snapshot (picker sessions: the
   * form's feature_source `where`). Applies to layers built after the call: set it before `activateForProject`.
   */
  setDrawFilter(layerId: string, filter: LiveDrawFilter | null): void {
    if (filter) this.drawFilters.set(layerId, filter);
    else this.drawFilters.delete(layerId);
  }

  /** Redraws a layer's live/filtered features with fresh symbols (e.g. once its draw filter's statuses arrive). */
  restyleDrawing(layerId: string): void {
    this.liveOverlays.get(layerId)?.restyle();
  }

  /** Hide all reference layers from the map without tearing them down. */
  hideFromMap(): void {
    for (const entry of this.activeLayers.values()) {
      entry.layer.visible = false;
    }
  }

  deactivate(mapView: __esri.MapView = this.mapView): void {
    this.activation++;
    this.controller.abort();
    this.controller = new AbortController();
    for (const [, entry] of this.activeLayers) {
      try {
        if (mapView?.map.layers.includes(entry.layer as any)) {
          mapView.map.remove(entry.layer);
        }
      } catch { /* ignore */ }
      entry.layer.destroy();
    }
    // buildGeoJsonLayer stores blob URLs keyed like `<summaryId>:<geomType>`,
    // so a simple get(summaryId) misses them. Revoke every tracked URL.
    for (const url of this.blobUrls.values()) URL.revokeObjectURL(url);
    this.activeLayers.clear();
    this.legacySummaries = [];
    this.blobUrls.clear();
    this.pending.clear();
    this.grantTimers.forEach(timer => clearTimeout(timer));
    this.grantTimers.clear();
    this.liveOverlays.forEach(overlay => overlay.destroy());
    this.liveOverlays.clear();
    this.publishedProject = false;
    this.catalogRevision = null;
    this.preferenceVersion = 0;
    this.publishedOverrides = {};
    this.confirmedOverrides = {};
    this.preferenceEdits.splice(0).forEach(edit => edit.resolve());
    this.preferenceRunning = false;
    this.accountId = undefined;
    this.projectId = undefined;
    this.currentAccount = () => undefined;
    this.manifest = undefined;
    this.manifestEtag = undefined;
  }

  private async buildLayerFromSummary(
    projectId: string,
    summary: ReferenceLayerSummary,
    index: number,
    signal: AbortSignal,
  ): Promise<__esri.Layer | null> {
    // Prefer the row's stable stored slot so publishing a catalog layer
    // (which shifts legacy rows' list position) doesn't repaint colors.
    const color = overlayColor(summary.palette_index ?? index);

    if (summary.source_type === 'arcgis_url') {
      if (!summary.source_url) return null;
      // Preserve the service renderer, including point and polygon symbology.
      return new FeatureLayer({
        id: `reference-layer-${summary.id}-content`,
        url: summary.source_url,
        title: summary.name,
        outFields: ['*'],
      });
    }

    if (summary.source_type === 'kml_url') {
      if (!summary.source_url) return null;
      // KMLLayer renders KML/KMZ natively — it reads the file's embedded
      // styles (per-placemark colors, icons, line widths) and surfaces them
      // without us applying an override renderer. Dynamic import keeps the
      // KML parser out of the main bundle until a project actually needs it.
      const KMLLayer = (await import('@arcgis/core/layers/KMLLayer')).default;
      return new KMLLayer({
        id: `reference-layer-${summary.id}-content`,
        url: summary.source_url,
        title: summary.name,
      });
    }

    if (summary.source_type === 'image_upload') return this.buildImageLayer(summary);

    // geojson_upload → fetch detail, render via blob-url GeoJSONLayer
    const detail = await firstValueFrom(this.referenceLayerApi.get(projectId, summary.id)
      .pipe(takeUntil(fromEvent(signal, 'abort'))));
    if (signal.aborted) return null;
    return this.buildGeoJsonLayer(detail, color);
  }

  private async buildImageLayer(summary: ReferenceLayerSummary): Promise<__esri.Layer | null> {
    if (!summary.source_url || !hasValidCorners(summary.image_georef)) return null;
    const [MediaLayer, ImageElement, CornersGeoreference, Point] = await Promise.all([
      import('@arcgis/core/layers/MediaLayer').then(module => module.default),
      import('@arcgis/core/layers/support/ImageElement').then(module => module.default),
      import('@arcgis/core/layers/support/CornersGeoreference').then(module => module.default),
      import('@arcgis/core/geometry/Point').then(module => module.default),
    ]);
    const corners = cornersToWebMercator(summary.image_georef.corners);
    const point = ([x, y]: [number, number]) => new Point({x, y, spatialReference: {wkid: 3857}});
    const element = new ImageElement({
      image: summary.source_url,
      georeference: new CornersGeoreference({
        topLeft: point(corners.topLeft), topRight: point(corners.topRight),
        bottomRight: point(corners.bottomRight), bottomLeft: point(corners.bottomLeft),
      }),
    });
    return new MediaLayer({
      id: `reference-layer-${summary.id}-content`, title: summary.name, source: [element], opacity: 1,
    });
  }

  private clearEntryResources(entry: ActiveRefLayer): void {
    if (this.activeLayers.get(entry.summary.id) !== entry) return;
    this.liveOverlays.get(entry.summary.id)?.destroy();
    this.liveOverlays.delete(entry.summary.id);
    for (const [key, timer] of this.grantTimers) {
      if (key.startsWith(`${entry.summary.id}:`)) { clearTimeout(timer); this.grantTimers.delete(key); }
    }
    for (const [key, url] of this.blobUrls) {
      if (key.startsWith(`${entry.summary.id}:`)) { URL.revokeObjectURL(url); this.blobUrls.delete(key); }
    }
  }

  /** Arbitrary dash arrays use the pinned renderer rather than an approximate SDK dash. */
  private vectorStyleSupported(style: ProjectMapStyle): boolean {
    return [...style.symbols, ...(style.uniqueValues?.values.map(value => value.symbol) || [])]
      .every(symbol => !symbol.dash?.length);
  }

  private selectedAssets(published: PublishedMapLayer): { asset: ProjectMapAsset; min: number; max: number }[] {
    const selected: { asset: ProjectMapAsset; min: number; max: number }[] = [];
    const vector = this.vectorStyleSupported(published.style)
      ? published.assets.find(asset => asset.kind === 'geojson' && asset.role === 'geometry' && asset.bytes <= 2 * 1024 * 1024)
      : undefined;
    if (vector) selected.push({ asset: vector, min: published.minZoom, max: published.maxZoom });
    for (const role of ['geometry', 'labels']) {
      if (role === 'geometry' && vector) continue;
      const candidates = published.assets.filter(asset => asset.kind === 'raster_xyz' && asset.role === role)
        .sort((left, right) => (right.maxZoom ?? published.maxZoom) - (left.maxZoom ?? published.maxZoom)
          || left.id.localeCompare(right.id));
      for (let zoom = published.minZoom; zoom <= published.maxZoom; zoom++) {
        if (role === 'labels' && (!published.style.label || zoom < published.style.label.minZoom || zoom > published.style.label.maxZoom)) continue;
        const asset = candidates.find(item => zoom >= (item.minZoom ?? published.minZoom) && zoom <= (item.maxZoom ?? published.maxZoom));
        if (!asset) {
          if (role === 'geometry') throw new Error('Published raster style has incomplete zoom coverage');
          continue;
        }
        const previous = selected[selected.length - 1];
        if (previous?.asset === asset && previous.max === zoom - 1) previous.max = zoom;
        else selected.push({ asset, min: zoom, max: zoom });
      }
    }
    return selected;
  }

  private async readPublishedGeoJson(url: string, asset: ProjectMapAsset, signal: AbortSignal): Promise<ReferenceLayerDetail['geojson']> {
    if (!Number.isSafeInteger(asset.bytes) || asset.bytes < 1 || asset.bytes > 2 * 1024 * 1024) throw new Error('Unsupported vector size');
    const response = await fetch(url, { signal, credentials: 'omit', redirect: 'error', cache: 'no-store' });
    if (!response.ok) throw Object.assign(new Error('Published GeoJSON could not be loaded'), { status: response.status });
    const declared = response.headers.get('content-length');
    if ((declared !== null && Number(declared) !== asset.bytes) || !response.body) {
      await response.body?.cancel(); throw new Error('Published GeoJSON size is inconsistent');
    }
    const reader = response.body.getReader();
    const bytes = new Uint8Array(asset.bytes);
    let offset = 0;
    try {
      while (true) {
        const { done, value } = await reader.read();
        signal.throwIfAborted();
        if (done) break;
        if (offset + value.byteLength > bytes.byteLength) throw new Error('Published GeoJSON exceeds its expected size');
        bytes.set(value, offset); offset += value.byteLength;
      }
      if (offset !== bytes.byteLength) throw new Error('Published GeoJSON was truncated');
      signal.throwIfAborted();
      if (CryptoJS.SHA256(CryptoJS.lib.WordArray.create(bytes)).toString() !== asset.sha256) {
        throw new Error('Published GeoJSON checksum is inconsistent');
      }
      return JSON.parse(new TextDecoder().decode(bytes));
    } finally { await reader.cancel(); reader.releaseLock(); }
  }

  private async buildPublishedLayer(entry: ActiveRefLayer, signal: AbortSignal): Promise<__esri.Layer | null> {
    const published = entry.published;
    if (!published || this.catalogRevision === null) return null;
    const scope = this.captureScope();
    const revision = this.catalogRevision;
    const content = new GroupLayer({ id: `published-map-layer-${published.id}-content`, title: published.name, visibilityMode: 'independent' });
    const refreshes: { asset: ProjectMapAsset; layer: WebTileLayer; expiresIn: number }[] = [];
    // A live-capable layer (live source, or a picker draw filter) is drawn from the features endpoint, so a missing
    // or failing snapshot asset only costs the fallback drawing: the layer stays, with live drawing attached.
    const filter = this.drawFilters.get(published.id);
    const liveCapable = filter ? filteredDrawEligible(published) : liveDrawEligible(published);
    const snapshotOptional = (error: unknown) => liveCapable && this.isCurrent(scope) && this.activeLayers.get(entry.summary.id) === entry
      && ![401, 403].includes((error as { status?: number })?.status);
    try {
      let assets: ReturnType<MapReferenceLayerService['selectedAssets']>;
      try {
        assets = this.selectedAssets(published);
      } catch (error) {
        if (!snapshotOptional(error)) throw error;
        assets = [];
      }
      for (const { asset, min, max } of assets) {
        try {
          this.requireScope(scope);
          const grant = await this.manifestApi.grant(scope.projectId, published.id, asset.renditionId || asset.id, revision, signal);
          this.requireScope(scope);
          if (this.activeLayers.get(entry.summary.id) !== entry) throw new Error('Map layer was replaced');
          if (asset.kind === 'raster_xyz') {
            if (!grant.grant) throw new Error('Published tile grant is unavailable');
            // Tiles stop at the layer's top zoom. Past it, enlarge those tiles (as the
            // mobile map does) instead of hiding the layer: no upper scale limit, and a
            // tile scheme that ends at that zoom so nothing deeper is requested.
            const topLevel = max === published.maxZoom && Number.isInteger(max);
            const layer = new WebTileLayer({
              id: `published-map-${published.id}-${asset.id}-${min}-${max}`,
              title: published.name,
              urlTemplate: this.manifestApi.tileUrl(grant.grant, '{level}', '{col}', '{row}'),
              visible: asset.role !== 'labels' || this.layerState(published).labels,
              opacity: 1,
              minScale: zoomScale(min), maxScale: topLevel ? 0 : zoomScale(max),
              ...(topLevel ? { tileInfo: TileInfo.create({ size: asset.tileSize || 256, numLODs: max + 1 }) } : {}),
            });
            (layer as WebTileLayer & { __projectMapLabelLayer: boolean }).__projectMapLabelLayer = asset.role === 'labels';
            content.add(layer);
            refreshes.push({ asset, layer, expiresIn: grant.expiresIn });
          } else {
            if (!grant.url) throw new Error('Published GeoJSON grant is unavailable');
            const geojson = await this.readPublishedGeoJson(grant.url, asset, signal);
            this.requireScope(scope);
            const vector = this.buildGeoJsonLayer({ id: published.id, name: published.name, geojson } as ReferenceLayerDetail,
              overlayColor([...this.activeLayers.keys()].indexOf(published.id)), published.style);
            if (vector) content.add(vector);
          }
        } catch (error) {
          if (!snapshotOptional(error)) throw error;
          console.warn(`Snapshot drawing unavailable for map layer ${published.id}; drawing live only`, error);
        }
      }
      this.requireScope(scope);
      refreshes.forEach(item => this.scheduleGrantRefresh(entry, item.asset, item.layer, item.expiresIn, scope, revision));
      if (content.layers.length || liveCapable) this.attachLiveDrawing(entry, content, scope, revision);
      if (content.layers.length) return content;
      content.destroy(); return null;
    } catch (error) {
      content.destroy();
      this.clearEntryResources(entry);
      throw error;
    }
  }

  /**
   * Live-first drawing (Sri: ArcGIS-backed layers always try live, fall back to the snapshot): features added to the
   * live source after the last capture are pickable through the live-first features endpoint, so draw them too.
   * The snapshot drawing stays in the group and comes back whenever the live answer does not hold.
   */
  private attachLiveDrawing(entry: ActiveRefLayer, content: GroupLayer, scope: MapScope, revision: number): void {
    const published = entry.published;
    const view = this.mapView;
    const filter = published ? this.drawFilters.get(published.id) || null : null;
    const isLabels = (layer: __esri.Layer) => !!(layer as __esri.Layer & { __projectMapLabelLayer?: boolean }).__projectMapLabelLayer;
    if (filter) {
      // Snapshot label tiles would label non-matching features too; a filtered layer has none (their grant refresh
      // stops on the destroyed layer).
      content.layers.toArray().filter(isLabels).forEach(layer => { content.remove(layer); layer.destroy(); });
    }
    const snapshot = content.layers.toArray().filter(layer => !isLabels(layer));
    if (filter && (!view || !filteredDrawEligible(published))) {
      // Filtered but not servable by the features endpoint: draw nothing rather than every feature.
      snapshot.forEach(layer => { layer.visible = false; });
      filter.onDrawn?.(null);
      return;
    }
    if (!view || !(filter ? filteredDrawEligible(published) : liveDrawEligible(published))) return;
    const color = overlayColor([...this.activeLayers.keys()].indexOf(published.id));
    const overlay = new LivePublishedFeatureOverlay({
      view, published, catalogRevision: revision, host: entry.layer, snapshot, filter,
      fetch: (bbox, limit, signal, cursor) => this.manifestApi.features(scope.projectId, published.id, revision, bbox, limit, signal, cursor),
      symbol: (geometry, properties, featureId) => {
        const status = geometry === 'point' ? filter?.status?.(featureId, properties) : null;
        if (status) return statusMarkerSymbol(status, published.style);
        const geomType = geometry === 'line' ? 'polyline' : geometry;
        const symbol = publishedSymbolFor(published.style, geometry, properties);
        return symbol ? this.buildPublishedSymbol(geomType, symbol) : this.buildSymbol(geomType, color, published.style);
      },
    });
    this.liveOverlays.get(entry.summary.id)?.destroy();
    this.liveOverlays.set(entry.summary.id, overlay);
    content.add(overlay.layer);
    overlay.start();
  }

  private scheduleGrantRefresh(entry: ActiveRefLayer, asset: ProjectMapAsset, layer: WebTileLayer, expiresIn: number,
    scope: MapScope, revision: number, failures = 0): void {
    const key = `${entry.summary.id}:${layer.id}`;
    const prior = this.grantTimers.get(key);
    if (prior) clearTimeout(prior);
    // Transient outages retain healthy tiles and retry at most once per minute.
    const wait = failures ? Math.min(60000, 1000 * 2 ** Math.min(failures, 6))
      : Math.max(1000, ((expiresIn > 0 ? expiresIn : 60) - 30) * 1000);
    const current = () => this.isCurrent(scope) && this.publishedProject && this.catalogRevision === revision
      && this.activeLayers.get(entry.summary.id) === entry && !layer.destroyed;
    const timer = setTimeout(async () => {
      if (this.grantTimers.get(key) !== timer) return;
      this.grantTimers.delete(key);
      if (!current()) return;
      try {
        const refreshed = await this.manifestApi.grant(scope.projectId, entry.published.id,
          asset.renditionId || asset.id, revision, scope.signal);
        if (!current()) return;
        if (!refreshed.grant) throw new Error('Tile grant is unavailable');
        layer.urlTemplate = this.manifestApi.tileUrl(refreshed.grant, '{level}', '{col}', '{row}');
        if (failures) layer.refresh();
        entry.error = undefined;
        this.scheduleGrantRefresh(entry, asset, layer, refreshed.expiresIn, scope, revision);
        this.changed.next();
      } catch (error) {
        if (!current()) return;
        if ([401, 403].includes(error?.status)) { this.deactivate(); this.changed.next(); return; }
        if ([404, 409].includes(error?.status)) {
          entry.layer.visible = false;
          entry.layer.destroy();
          this.mapView?.map.remove(entry.layer);
          this.clearEntryResources(entry);
          this.activeLayers.delete(entry.summary.id);
        } else {
          entry.error = 'Tile access could not be refreshed. Retrying shortly.';
          this.scheduleGrantRefresh(entry, asset, layer, expiresIn, scope, revision, failures + 1);
        }
        this.changed.next();
      }
    }, wait);
    this.grantTimers.set(key, timer);
  }

  private buildGeoJsonLayer(
    detail: ReferenceLayerDetail,
    color: [number, number, number, number],
    style?: ProjectMapStyle,
  ): __esri.Layer | null {
    if (!detail?.geojson?.features?.length) return null;

    // ArcGIS GeoJSONLayer has two hard constraints we need to work around:
    //   1. GeometryCollection features are dropped entirely (even if each
    //      sub-geometry is a supported type).
    //   2. A layer can only hold features of one geometry type — mixed
    //      FeatureCollections have all non-matching features dropped.
    // We flatten GeometryCollections into individual features, bucket by
    // geometry type, and return a GroupLayer containing one GeoJSONLayer per
    // bucket. The GroupLayer keeps the summary→ArcGIS-layer 1:1 mapping the
    // rest of this service relies on.
    const flattened = this.flattenGeometryCollections(detail.geojson.features);
    const buckets = this.bucketByGeometryType(flattened);
    const children: GeoJSONLayer[] = [];
    (['point', 'polyline', 'polygon'] as const).forEach((geomType) => {
      const features = buckets[geomType];
      if (!features?.length) return;
      const blob = new Blob(
        [JSON.stringify({ type: 'FeatureCollection', features })],
        { type: 'application/json' },
      );
      const url = URL.createObjectURL(blob);
      this.blobUrls.set(`${detail.id}:${geomType}`, url);
      const defaultSymbol = this.buildSymbol(geomType, color, style);
      const unique = style?.uniqueValues;
      const renderer = unique ? new UniqueValueRenderer({
        field: unique.field,
        defaultSymbol,
        uniqueValueInfos: unique.values
          .filter(value => value.symbol.geometry === (geomType === 'polyline' ? 'line' : geomType))
          .map(value => ({ value: value.value, symbol: this.buildPublishedSymbol(geomType, value.symbol) })),
      }) : new SimpleRenderer({ symbol: defaultSymbol });
      children.push(new GeoJSONLayer({
        id: `reference-layer-${detail.id}-${geomType}`,
        url,
        title: detail.name,
        geometryType: geomType,
        popupEnabled: !style,
        renderer,
      }));
    });

    if (!children.length) return null;
    return new GroupLayer({
      id: `reference-layer-${detail.id}-content`,
      title: detail.name,
      layers: children,
    });
  }

  /**
   * Expand GeometryCollection features into one feature per contained
   * geometry, preserving the parent's `properties`. Non-collection features
   * pass through unchanged. Features whose geometry is null are dropped so
   * they don't count as "bucketable" and inflate feature counts.
   */
  private flattenGeometryCollections(features: any[]): any[] {
    const out: any[] = [];
    for (const f of features) {
      const g = f?.geometry;
      if (!g) continue;
      if (g.type === 'GeometryCollection') {
        for (const sub of g.geometries || []) {
          if (!sub) continue;
          out.push({ ...f, geometry: sub });
        }
      } else {
        out.push(f);
      }
    }
    return out;
  }

  /**
   * Group GeoJSON features by the three geometry-type buckets ArcGIS
   * GeoJSONLayer supports. (Multi*) rolls up into its non-multi bucket.
   */
  private bucketByGeometryType(features: any[]): {
    point: any[]; polyline: any[]; polygon: any[];
  } {
    const buckets = { point: [] as any[], polyline: [] as any[], polygon: [] as any[] };
    for (const f of features) {
      const t = f?.geometry?.type;
      if (t === 'Point' || t === 'MultiPoint') buckets.point.push(f);
      else if (t === 'LineString' || t === 'MultiLineString') buckets.polyline.push(f);
      else if (t === 'Polygon' || t === 'MultiPolygon') buckets.polygon.push(f);
    }
    return buckets;
  }

  private buildSymbol(
    geomType: 'point' | 'polyline' | 'polygon',
    color: [number, number, number, number],
    style?: ProjectMapStyle,
  ): any {
    const selected = style?.symbols.find(symbol => symbol.geometry === (geomType === 'polyline' ? 'line' : geomType));
    if (selected) return this.buildPublishedSymbol(geomType, selected);
    if (geomType === 'polyline') return this.buildLineSymbol(color);
    if (geomType === 'polygon') {
      return new SimpleFillSymbol({
        color: [color[0], color[1], color[2], 0.3],
        outline: { color, width: 2 } as any,
      });
    }
    return new SimpleMarkerSymbol({
      color,
      size: 8,
      outline: { color: [255, 255, 255, 0.9], width: 1 } as any,
    });
  }

  private buildLineSymbol(color: [number, number, number, number]) {
    return new SimpleLineSymbol({ color, width: 2.5 });
  }

  private buildPublishedSymbol(geomType: 'point' | 'polyline' | 'polygon', symbol: ProjectMapStyle['symbols'][number]) {
    const parse = (value: string, alpha = 1) => {
      const hex = value.replace('#', '');
      const inherent = hex.length === 8 ? parseInt(hex.slice(6), 16) / 255 : 1;
      return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16), inherent * alpha];
    };
    const width = symbol.strokeWidth ?? (geomType === 'point' ? 0 : 1);
    const outline = { color: parse(symbol.strokeColor ?? symbol.color), width: `${width}px` };
    if (geomType === 'point') return new SimpleMarkerSymbol({ color: parse(symbol.color), size: `${symbol.size ?? 8}px`, outline });
    if (geomType === 'polyline') return new SimpleLineSymbol({ ...outline, style: 'solid' });
    return new SimpleFillSymbol({ color: parse(symbol.color, symbol.fillAlpha ?? 1), outline });
  }
}
